/**
 * Client-side helpers for parsing user-supplied GeoJSON files and producing the geometries
 * we render on the workspace map. The backend re-validates everything on submit; these
 * helpers exist purely so the user sees an inline error before they press save.
 */

export type GeoJsonValidationCode =
  | 'invalid-json'
  | 'not-an-object'
  | 'unsupported-type'
  | 'feature-collection-not-singleton'
  | 'missing-geometry'
  | 'not-a-polygon'
  | 'missing-coordinates'
  | 'ring-too-short'
  | 'ring-not-closed'
  | 'position-out-of-range';

export interface GeoJsonValidationError {
  readonly code: GeoJsonValidationCode;
  readonly featureCount?: number;
  readonly type?: string;
}

export interface ParsedGeoJson {
  /** The original parsed JSON, suitable to round-trip to the backend. */
  readonly raw: object;
  /** Normalised GeoJSON Feature with a single Polygon / MultiPolygon, used by previews. */
  readonly normalisedFeature: GeoJsonFeature;
}

export interface GeoJsonFeature {
  readonly type: 'Feature';
  readonly properties: Record<string, unknown>;
  readonly geometry: GeoJsonPolygon | GeoJsonMultiPolygon;
}

export interface GeoJsonPolygon {
  readonly type: 'Polygon';
  readonly coordinates: [number, number][][];
}

export interface GeoJsonMultiPolygon {
  readonly type: 'MultiPolygon';
  readonly coordinates: [number, number][][][];
}

const MIN_RING_POSITIONS = 4;

export function parseAreaGeoJsonString(input: string): ParsedGeoJson {
  let parsed: unknown;
  try {
    parsed = JSON.parse(input);
  } catch {
    throw asError({ code: 'invalid-json' });
  }
  return parseAreaGeoJson(parsed);
}

export function parseAreaGeoJson(raw: unknown): ParsedGeoJson {
  if (!raw || typeof raw !== 'object') {
    throw asError({ code: 'not-an-object' });
  }
  const root = raw as Record<string, unknown>;
  const feature = extractFeature(root);
  return { raw: root, normalisedFeature: feature };
}

function extractFeature(node: Record<string, unknown>): GeoJsonFeature {
  const type = node['type'];
  if (type === 'FeatureCollection') {
    const features = node['features'];
    if (!Array.isArray(features) || features.length !== 1) {
      throw asError({
        code: 'feature-collection-not-singleton',
        featureCount: Array.isArray(features) ? features.length : 0,
      });
    }
    return normaliseFeature(features[0]);
  }
  if (type === 'Feature') {
    return normaliseFeature(node);
  }
  if (type === 'Polygon' || type === 'MultiPolygon') {
    return normaliseFeature({ type: 'Feature', geometry: node, properties: {} });
  }
  throw asError({ code: 'unsupported-type', type: typeof type === 'string' ? type : undefined });
}

function normaliseFeature(value: unknown): GeoJsonFeature {
  if (!value || typeof value !== 'object') {
    throw asError({ code: 'not-an-object' });
  }
  const node = value as Record<string, unknown>;
  const geometry = node['geometry'];
  if (!geometry || typeof geometry !== 'object') {
    throw asError({ code: 'missing-geometry' });
  }
  const geom = geometry as Record<string, unknown>;
  const geomType = geom['type'];
  if (geomType !== 'Polygon' && geomType !== 'MultiPolygon') {
    throw asError({ code: 'not-a-polygon', type: typeof geomType === 'string' ? geomType : undefined });
  }
  const coordinates = geom['coordinates'];
  if (!Array.isArray(coordinates) || coordinates.length === 0) {
    throw asError({ code: 'missing-coordinates' });
  }
  if (geomType === 'Polygon') {
    const rings = validatePolygonRings(coordinates);
    return {
      type: 'Feature',
      properties: (node['properties'] as Record<string, unknown> | undefined) ?? {},
      geometry: { type: 'Polygon', coordinates: rings },
    };
  }
  const polys = coordinates.map((p) => validatePolygonRings(p));
  return {
    type: 'Feature',
    properties: (node['properties'] as Record<string, unknown> | undefined) ?? {},
    geometry: { type: 'MultiPolygon', coordinates: polys },
  };
}

function validatePolygonRings(coords: unknown): [number, number][][] {
  if (!Array.isArray(coords) || coords.length === 0) {
    throw asError({ code: 'missing-coordinates' });
  }
  return coords.map((ring): [number, number][] => {
    if (!Array.isArray(ring) || ring.length < MIN_RING_POSITIONS) {
      throw asError({ code: 'ring-too-short' });
    }
    const positions = ring.map((position): [number, number] => {
      if (!Array.isArray(position) || position.length < 2) {
        throw asError({ code: 'position-out-of-range' });
      }
      // `Number("abc")` is NaN — the `Number.isFinite` guard below rejects non-numeric input
      // before we ever compare it to the lon/lat bounds, mirroring the backend's isNumber gate.
      const lon = Number(position[0]);
      const lat = Number(position[1]);
      if (
        !Number.isFinite(lon) ||
        !Number.isFinite(lat) ||
        lon < -180 ||
        lon > 180 ||
        lat < -90 ||
        lat > 90
      ) {
        throw asError({ code: 'position-out-of-range' });
      }
      return [lon, lat];
    });
    // RFC 7946 §3.1.6: a linear ring's first and last position must be identical. Catching
    // this here lets the user see "close your polygon" inline before they press save, instead
    // of round-tripping to the backend just to get the same error.
    const first = positions[0];
    const last = positions[positions.length - 1];
    if (!first || !last) {
      throw asError({ code: 'ring-too-short' });
    }
    if (first[0] !== last[0] || first[1] !== last[1]) {
      throw asError({ code: 'ring-not-closed' });
    }
    return positions;
  });
}

export function isGeoJsonValidationError(value: unknown): value is GeoJsonValidationError {
  if (!value || typeof value !== 'object') return false;
  return typeof (value as { code?: unknown }).code === 'string';
}

function asError(error: GeoJsonValidationError): GeoJsonValidationError {
  return error;
}

/**
 * Build a minimal GeoJSON Feature wrapping a list of [lon, lat] positions captured from
 * user clicks on the Draw tab. The resulting object is what we POST to the backend.
 */
export function buildPolygonFeatureFromVertices(
  vertices: readonly [number, number][],
): GeoJsonFeature {
  const first = vertices.at(0);
  const last = vertices.at(-1);
  const closed: [number, number][] =
    first && last && vertices.length >= 3 && !samePosition(first, last)
      ? [...vertices, first]
      : [...vertices];
  return {
    type: 'Feature',
    properties: {},
    geometry: { type: 'Polygon', coordinates: [closed] },
  };
}

function samePosition(a: [number, number], b: [number, number]): boolean {
  return a[0] === b[0] && a[1] === b[1];
}
